import React from 'react'
import { SubmissionTypes } from '@oneblink/types'
import { OneBlinkForm } from '@oneblink/apps-react'
import { OneBlinkAppsError, submissionService } from '@oneblink/apps'
import { OneBlinkReadOnlyForm } from '@oneblink/apps-react'
import styled from 'styled-components'
import { OpenInNew, Info, Reply } from '@styled-icons/material'

import useQuery from 'hooks/useQuery'
import useOneBlinkError from 'hooks/useOneBlinkError'
import useSubmitForm from 'hooks/useSubmitForm'
import useFetchForm from 'hooks/useFetchForm'

import { H2, P } from 'components/TextComponents'
import ErrorMessage from 'components/ErrorMessage'
import LoadingSpinner from 'components/LoadingSpinner'
import { postRequest } from 'lib/service/fetchService'
import BlankModal from '../../components/BlankModal'
import { mergeSubmissions } from '../../lib/submission/merge-vet-dec'
import SubmissionModals from '../../Scenes/Form/SubmissionModals'
import VetDecPage from './VetDecPage'
import Footer from '../../components/Layout/Footer'
import { ChildrenContainer } from '../../components/Layout/Page'

import config from 'config'

const VetValidationContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`
VetValidationContainer.displayName = 'VetValidationContainer'

const VetValidationForm = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  button {
    margin-top: 16px;
  }
  padding-bottom: 16px;
`
VetValidationForm.displayName = 'VetValidationForm'

const FormContainer = styled.div`
  padding: ${({ theme }) => theme.unit / 2}px;
`
FormContainer.displayName = 'FormContainer'

const DeclarationModalButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.4rem 0px;
  cursor: pointer;
  border: none;
  background-color: ${({ theme }) => theme.palette.nswPaletteGrey04};
  font-size: 1rem;
  padding: ${({ theme }) => theme.unit}px;
`
DeclarationModalButton.displayName = 'DeclarationModalButton'

const OpenIcon = styled(OpenInNew)`
  color: ${({ theme }) => theme.palette.nswTextDark};
  height: 2.5rem;
`
OpenIcon.displayName = 'OpenIcon'

const InfoIcon = styled(Info)`
  color: ${({ theme }) => theme.palette.nswTextDark};
  height: 2.5rem;
  margin-right: ${({ theme }) => theme.unit}px;
`
InfoIcon.displayName = 'InfoIcon'

const BackIcon = styled(Reply)`
  color: ${({ theme }) => theme.palette.nswTextLight};
  height: 1.5rem;
  margin-right: ${({ theme }) => theme.unit / 2}px;
`
BackIcon.displayName = 'BackIcon'

const BackToFormButton = styled.button`
  border-radius: 4px;
  padding: ${({ theme }) => theme.unit / 2}px;
  border: none;
  max-width: fit-content;
  font-size: 1rem;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.palette.nswTextLight};
  background-color: ${({ theme }) => theme.palette.nswBrandDark};
  cursor: pointer;
`
BackToFormButton.displayName = 'BackToFormButton'

const ModalInfo = styled.div`
  padding: ${({ theme }) => theme.unit}px;
  display: flex;
  flex-direction: row;
  background-color: ${({ theme }) => theme.palette.nswPaletteOffWhite};
  margin: ${({ theme }) => theme.unit / 2}px 0;
  align-items: center;
`
ModalInfo.displayName = 'ModalInfo'

const VetDecSubHeader = styled.div`
  width: 100%;
  padding: 0 ${({ theme }) => theme.unit}px;
  background-color: ${({ theme }) => theme.palette.nswPaletteGrey04};
  color: ${({ theme }) => theme.palette.nswTextDark};
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  z-index: 3;
`
VetDecSubHeader.displayName = 'VetDecSubHeader'

export default function ValidationContainer() {
  const [originalSubmissionData, setOriginalSubmissionData] =
    React.useState<SubmissionTypes.S3SubmissionData>()

  return (
    <VetDecPage>
      {originalSubmissionData && (
        <VetDecSubHeader>
          <H2>Original Submission (Read-only)</H2>
        </VetDecSubHeader>
      )}
      <ChildrenContainer>
        <Validation
          setOriginalSubmissionData={setOriginalSubmissionData}
          originalSubmissionData={originalSubmissionData}
        />
      </ChildrenContainer>
    </VetDecPage>
  )
}

function Validation({
  setOriginalSubmissionData,
  originalSubmissionData,
}: {
  setOriginalSubmissionData: React.Dispatch<
    React.SetStateAction<SubmissionTypes.S3SubmissionData | undefined>
  >
  originalSubmissionData?: SubmissionTypes.S3SubmissionData
}) {
  const query = useQuery()
  const submissionId = query.submissionId?.toString()
  const trackingCode = query.trackingCode?.toString()
  const vetDecFormId = config.VET_DEC_FORM_ID

  const [vetEmail, setVetEmail] = React.useState('')

  const [isValidating, setIsValidating] = React.useState(false)
  const [validationError, setValidationError] = useOneBlinkError()
  const { formDefinition, fetchFormError, isFetchingForm } =
    useFetchForm(vetDecFormId)
  const [isModalOpen, setIsModalOpen] = React.useState(false)

  const {
    onSubmit,
    isSubmitting,
    formSubmissionResult,
    submissionError,
    setSubmissionError,
    handlePostSubmissionAction,
    postSubmissionActionErrorMessage,
  } = useSubmitForm()

  const vetLookUp = React.useCallback(async () => {
    setIsValidating(true)
    setOriginalSubmissionData(undefined)
    const url = `${config.OB_HOSTED_API_URL}/submissions/${submissionId}`
    const resource = { email: vetEmail }
    try {
      const resp = await postRequest<SubmissionTypes.S3SubmissionData>({
        url,
        resource,
      })
      setOriginalSubmissionData(resp.body)
    } catch (e) {
      setValidationError(e as OneBlinkAppsError)
    }
    setIsValidating(false)
  }, [
    vetEmail,
    submissionId,
    setOriginalSubmissionData,
    setValidationError,
    setIsValidating,
  ])

  const handleSubmit = async (
    newFormSubmission: submissionService.NewFormSubmission,
  ) => {
    if (originalSubmissionData) {
      const mergedSubmission = mergeSubmissions(
        originalSubmissionData,
        newFormSubmission,
      )

      const existingTrackingCode = originalSubmissionData.externalId

      const submitFn = onSubmit({ existingTrackingCode })

      await submitFn(mergedSubmission)
    }
  }

  if (!submissionId) {
    return <div>Missing submission Id, please check the link in your email</div>
  }

  if (!trackingCode) {
    return (
      <div>Missing reference code, please check the link in your email</div>
    )
  }

  if (!originalSubmissionData) {
    return (
      <VetValidationContainer>
        <H2>Confirm your details</H2>
        <P>
          Before you complete the vet declaration for{' '}
          <strong>{trackingCode}</strong> please verify your email address below
        </P>

        <VetValidationForm>
          <label>
            <strong>Email address</strong>
            <input
              className="input"
              value={vetEmail}
              onChange={(e) => setVetEmail(e.target.value)}
            />
          </label>
          <button
            type="button"
            className="button ob-button is-primary"
            onClick={vetLookUp}
            disabled={isValidating}
          >
            Verify email
          </button>
        </VetValidationForm>
        {isValidating && <LoadingSpinner>Verifying</LoadingSpinner>}
        {validationError && (
          <ErrorMessage>{validationError.message}</ErrorMessage>
        )}
      </VetValidationContainer>
    )
  }

  if (isFetchingForm) {
    return <LoadingSpinner>Fetching data</LoadingSpinner>
  }

  if (fetchFormError) {
    return (
      <ErrorMessage>
        An error has occurred while attempting to load this form
      </ErrorMessage>
    )
  }

  if (!formDefinition) {
    return <ErrorMessage>Unable to find form</ErrorMessage>
  }

  return (
    <>
      <FormContainer className={originalSubmissionData.definition.description}>
        <OneBlinkReadOnlyForm
          form={originalSubmissionData.definition}
          initialSubmission={originalSubmissionData.submission}
        />
      </FormContainer>
      <BlankModal isOpen={isModalOpen} desktopContentWidth="50%">
        <BackToFormButton onClick={() => setIsModalOpen(false)}>
          <BackIcon />
          Return to original submission
        </BackToFormButton>
        <ModalInfo>
          <InfoIcon />
          <>
            The above button may be clicked at any time if you need to refer
            back to the original submission. All progress will be saved.
          </>
        </ModalInfo>
        <H2>Your declaration</H2>
        <FormContainer className={formDefinition.description}>
          <OneBlinkForm
            form={formDefinition}
            initialSubmission={{
              submissionId__hidden: submissionId,
            }}
            onCancel={() => setIsModalOpen(false)}
            onSubmit={handleSubmit}
            disabled={isSubmitting}
            isPendingQueueEnabled={false}
          />
          <SubmissionModals
            isSubmitting={isSubmitting}
            formSubmissionResult={formSubmissionResult}
            submissionError={submissionError}
            setSubmissionError={setSubmissionError}
            handlePostSubmissionAction={handlePostSubmissionAction}
            postSubmissionActionErrorMessage={postSubmissionActionErrorMessage}
          />
        </FormContainer>
      </BlankModal>
      <Footer>
        <DeclarationModalButton onClick={() => setIsModalOpen(true)}>
          <OpenIcon /> Open and complete your declaration
        </DeclarationModalButton>
      </Footer>
    </>
  )
}
